import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { SessionPicker } from "@/components/masterclass/SessionPicker";
import { fmtISTDate, fmtISTTime } from "@/lib/ist";

const REVENUE_BANDS = [
  "Under ₹50 Lakh", "₹50 Lakh – ₹2 Crore", "₹2 – ₹10 Crore", "₹10 Crore+", "Prefer not to say",
];

const EMPTY = {
  name: "", email: "", phone: "", city: "",
  business_name: "", annual_revenue: "", biggest_challenge: "",
};

function Field({ id, label, testId, value, onChange, type = "text", required }) {
  return (
    <div>
      <Label htmlFor={id}>{label}{required ? " *" : ""}</Label>
      <Input
        id={id} type={type} required={required} data-testid={testId}
        value={value} onChange={onChange} className="mt-1.5 h-11 bg-white"
      />
    </div>
  );
}

function Success({ name, session }) {
  return (
    <div data-testid="masterclass-form-success" className="py-16 text-center">
      <div className="w-16 h-16 mx-auto rounded-full bg-navy flex items-center justify-center mb-6">
        <Check size={28} className="text-white" />
      </div>
      <h3 className="font-display text-3xl text-navy">Your seat is saved{name ? `, ${name.split(" ")[0]}` : ""}.</h3>
      {session && (
        <p data-testid="masterclass-success-session" className="mt-3 font-semibold text-navy">
          {fmtISTDate(session.starts_at)} · {fmtISTTime(session.starts_at)} IST
        </p>
      )}
      <p className="mt-3 text-gray-600 max-w-sm mx-auto">
        Check your inbox for your confirmation. Your joining link comes by email, and we'll remind you 24 hours and 1 hour before we go live.
      </p>
    </div>
  );
}

function NoSessions() {
  return (
    <div data-testid="masterclass-no-sessions" className="py-12 text-center">
      <h3 className="font-display text-2xl text-navy">New dates coming soon</h3>
      <p className="mt-3 text-gray-600 max-w-sm mx-auto">All current sessions are full or finished. Get in touch and we'll let you know when the next one opens.</p>
      <Link to="/contact" data-testid="masterclass-no-sessions-contact" className="mt-6 inline-flex bg-navy text-white font-semibold px-6 py-3 rounded-full hover:bg-navy-deep transition-colors">Contact us</Link>
    </div>
  );
}

export const RegisterForm = ({ sessions }) => {
  const firstOpen = sessions.find((s) => s.seats_left > 0);
  const [form, setForm] = useState({ ...EMPTY, session_id: firstOpen?.id || "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/masterclass", form);
      setDone(true);
      toast.success("You're registered for the masterclass.");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <Success name={form.name} session={sessions.find((s) => s.id === form.session_id)} />;
  if (!firstOpen) return <NoSessions />;

  return (
    <form data-testid="masterclass-form" onSubmit={onSubmit} className="space-y-5">
      <h3 className="font-display text-2xl text-navy">Reserve your free seat</h3>
      <SessionPicker sessions={sessions} value={form.session_id} onChange={(id) => setForm((f) => ({ ...f, session_id: id }))} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="mc-name" label="Full name" testId="masterclass-input-name" value={form.name} onChange={update("name")} required />
        <Field id="mc-phone" label="Phone / WhatsApp" testId="masterclass-input-phone" value={form.phone} onChange={update("phone")} required />
      </div>
      <Field id="mc-email" type="email" label="Email" testId="masterclass-input-email" value={form.email} onChange={update("email")} required />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="mc-city" label="City" testId="masterclass-input-city" value={form.city} onChange={update("city")} required />
        <Field id="mc-business" label="Business name" testId="masterclass-input-business-name" value={form.business_name} onChange={update("business_name")} />
      </div>
      <div>
        <Label htmlFor="mc-revenue">Annual revenue</Label>
        <Select value={form.annual_revenue} onValueChange={(v) => setForm((f) => ({ ...f, annual_revenue: v }))}>
          <SelectTrigger id="mc-revenue" data-testid="masterclass-input-revenue" className="mt-1.5 h-11 bg-white">
            <SelectValue placeholder="Select a range" />
          </SelectTrigger>
          <SelectContent>
            {REVENUE_BANDS.map((b) => (
              <SelectItem key={b} value={b} data-testid={`masterclass-revenue-${REVENUE_BANDS.indexOf(b)}`}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="mc-challenge">Your biggest sales challenge right now</Label>
        <Textarea
          id="mc-challenge" rows={3} data-testid="masterclass-input-challenge"
          value={form.biggest_challenge} onChange={update("biggest_challenge")} className="mt-1.5 bg-white"
        />
      </div>
      {error && <div data-testid="masterclass-form-error" className="text-sm text-red-600">{error}</div>}
      <button
        type="submit" data-testid="masterclass-form-submit" disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold py-4 rounded-full hover:bg-navy-deep hover:-translate-y-0.5 transition disabled:opacity-60"
      >
        {submitting && <Loader2 size={16} className="animate-spin" />}
        Save my seat, it's free
      </button>
      <p className="text-[11px] text-gray-500 text-center">Live online session. Your details stay private.</p>
    </form>
  );
};
